import {
  ReadyPlanOptionGroupPersonalizationType,
  CheckoutConfigOptionGroupStep as Step,
} from "src/generated/graphqlTypes";
import { pluralize } from "src/utils";
import {
  ReviewConfigurationStep_CheckoutConfigFragment,
  ReviewConfigurationStep_OptionGroupFragment,
  ReviewConfigurationStep_ProgramDataFragment,
  ReviewConfigurationStep_StructureFragment,
} from "./2.ReviewConfigurationStep.generated";

/**
 * A review configuration group is a titled row with its own amount, and the selections that make it up.
 * i.e.
 * title: Exterior
 * amountInCents: 100000
 * items: [
 *   { title: "B - Spanish", amountInCents: 100000 },
 *   { title: "Soapstone", amountInCents: 100000 },
 * ]
 */
export type ReviewConfigurationGroup = {
  title: string;
  amountInCents: number;
  items: { title: string; amountInCents?: number }[];
};
// Then the section is what is passed to the FormSectionLayout component to render the sections, i.e. Plan & Exterior, Interior, Add-Ons.
type ReviewConfigurationSection = { title: string; groups: ReviewConfigurationGroup[] };

/** The half of a CheckoutConfig a given structure is on. A config covers both the main house and the ADU. */
type ReviewConfigurationDetail = {
  optionGroups: ReviewConfigurationStep_OptionGroupFragment[];
  salesPriceInCents: number;
  productOffering: ReviewConfigurationStep_CheckoutConfigFragment["productOffering"];
  programData: ReviewConfigurationStep_ProgramDataFragment | null | undefined;
};

/** The read-only breakdown of the configuration the structure would end up with. */
export function toReviewConfigurationSections(
  structure: ReviewConfigurationStep_StructureFragment,
  checkoutConfig: ReviewConfigurationStep_CheckoutConfigFragment,
): ReviewConfigurationSection[] {
  const { aduOptionGroups, aduSalesPriceInCents, aduProductOffering, aduProgramData } = checkoutConfig;
  const { mhOptionGroups, mhSalesPriceInCents, productOffering, programData, isReversed } = checkoutConfig;
  const detail: ReviewConfigurationDetail = structure.isAdu
    ? {
        optionGroups: aduOptionGroups,
        salesPriceInCents: aduSalesPriceInCents,
        productOffering: aduProductOffering,
        programData: aduProgramData,
      }
    : { optionGroups: mhOptionGroups, salesPriceInCents: mhSalesPriceInCents, productOffering, programData };
  const sections = structure.isAdu
    ? toAduSections(detail, isReversed)
    : toMainHouseSections(detail, isReversed, checkoutConfig.interiorSubSteps);
  return sections.filter((section) => section.groups.nonEmpty);
}

/**
 * Main house: "Plan & Exterior", then one section per interior sub-step, then "Add-Ons".
 *
 * The sub-step titles are the server's own `displayName`s ("Interior Package", "Kitchen", ...) because they are
 * plan-specific — hardcoding the design's names would break every other plan.
 */
function toMainHouseSections(
  detail: ReviewConfigurationDetail,
  isReversed: boolean,
  subSteps: ReviewConfigurationStep_CheckoutConfigFragment["interiorSubSteps"],
) {
  const all = detail.optionGroups;
  // Grouped by sub-step *number*, not step: the interior design package groups (Interior Scheme, Spec Level)
  // land on the first sub-step alongside the personalization ones. Mirrors `getInteriorPersonalizationGroupsBySubStep`.
  const bySubStep = selectedGroups(all)
    .filter((og) => !!og.subStepNumber)
    .groupBy((og) => og.subStepNumber!);

  return [
    {
      title: "Plan & Exterior",
      groups: [
        floorPlanGroup(detail, isReversed),
        toGroup("Exterior", selectedGroups(all, [Step.ExteriorElevation, Step.ExteriorDesignPackage]), all),
        toGroup("Floor Plan Options", selectedGroups(all, [Step.OptionsStructural]), all),
      ].compact(),
    },
    ...subSteps.map((subStep) => ({
      title: subStep.displayName,
      groups: (bySubStep[subStep.stepNumber] ?? []).map((og) => toGroup(og.group.name, [og], all)).compact(),
    })),
    {
      title: "Add-Ons",
      // One row per add-on rather than one "Add-Ons" row with sub-lines, per the design. Add-ons repeat across
      // rooms, so the room is what tells them apart.
      groups: selectedGroups(all, [Step.OptionsAddon]).map((og) => {
        const lines = toSelectionLines(og, all);
        const location = og.group.readyPlanLocation;
        const title = lines[0].title;
        return {
          title: location ? `${title} - ${location.marketingName}` : title,
          amountInCents: lines.sum((line) => line.amountInCents),
          items: [],
        };
      }),
    },
  ];
}

/**
 * ADU: every option group on the ADU product offering carries `step: ADU` (the backend overwrites the computed step
 * for ADU plans), so the main house's step-based mapping does not apply. Sections come from the option group's global
 * type instead, matching how the customer summary's ADU card groups them.
 */
function toAduSections(detail: ReviewConfigurationDetail, isReversed: boolean): ReviewConfigurationSection[] {
  // A config without an ADU product offering has an empty ADU side; the step says so instead of rendering blank rows
  if (!detail.productOffering) return [];
  const all = detail.optionGroups;
  const byCategory = selectedGroups(all).groupBy(toAduCategory);
  const group = (category: AduCategory) => toGroup(category, byCategory[category] ?? [], all);

  return [
    {
      title: "Plan & Exterior",
      groups: [floorPlanGroup(detail, isReversed), group("Elevation"), group("Exterior Scheme")].compact(),
    },
    { title: "Interior", groups: [group("Interior Scheme"), group("Spec Level")].compact() },
    {
      title: "Personalization",
      groups: (byCategory["Personalization"] ?? []).map((og) => toGroup(og.group.name, [og], all)).compact(),
    },
  ];
}

/** One row: its title, the total of everything under it, and a line per selection at its own price. */
function toGroup(
  title: string,
  groups: ReviewConfigurationStep_OptionGroupFragment[],
  allGroups: ReviewConfigurationStep_OptionGroupFragment[],
): ReviewConfigurationGroup | undefined {
  if (groups.isEmpty) return undefined;
  const lines = groups.flatMap((og) => toSelectionLines(og, allGroups));
  return { title, amountInCents: lines.sum((line) => line.amountInCents), items: lines };
}

/** A group's selected option, plus its swatch child's when one rolls up into it (a swatch child is never its own row). */
function toSelectionLines(
  optionGroup: ReviewConfigurationStep_OptionGroupFragment,
  allGroups: ReviewConfigurationStep_OptionGroupFragment[],
) {
  const child = allGroups.find((og) => og.group.id === optionGroup.childGroup?.id);
  const rollsUp = !!child?.selectedOption && usesSwatchSelector(child);
  return (rollsUp ? [optionGroup, child!] : [optionGroup]).map((og) => ({
    title: og.selectedOption!.option.name,
    amountInCents: og.selectedOption!.totalPriceInCents,
  }));
}

/** The plan itself, priced at the side's starting price. Its lines are descriptive, so they carry no amount. */
function floorPlanGroup(detail: ReviewConfigurationDetail, isReversed: boolean): ReviewConfigurationGroup {
  const { productOffering, programData, salesPriceInCents } = detail;
  return {
    title: "Floor Plan",
    amountInCents: salesPriceInCents,
    items: [
      productOffering && { title: `${productOffering.name} (Orientation: ${isReversed ? "Reversed" : "Standard"})` },
      { title: toProgramDataLine(programData) },
    ].compact(),
  };
}

/**
 * Option groups with a selection worth showing the customer, optionally narrowed to some steps.
 *
 * `showOnCustomerSummary`, not `isEnabled`: `isEnabled` flips false for personalization groups once a config is
 * reserved, which would silently drop real selections from the breakdown. Swatch children are dropped because they
 * are surfaced under their parent instead — `allGroups` still has them so `toSelectionLines` can find them.
 */
function selectedGroups(
  optionGroups: ReviewConfigurationStep_OptionGroupFragment[],
  steps?: Step[],
): ReviewConfigurationStep_OptionGroupFragment[] {
  return optionGroups.filter(
    (og) =>
      og.showOnCustomerSummary &&
      !!og.selectedOption &&
      !(og.parentGroup && usesSwatchSelector(og)) &&
      (!steps || steps.includes(og.step)),
  );
}

function usesSwatchSelector(optionGroup: ReviewConfigurationStep_OptionGroupFragment): boolean {
  const { personalizationType, displayPackageOptionsAsSingleProduct } = optionGroup.group;
  return (
    personalizationType?.code !== ReadyPlanOptionGroupPersonalizationType.Package ||
    !!displayPackageOptionsAsSingleProduct
  );
}

type AduCategory = "Elevation" | "Exterior Scheme" | "Interior Scheme" | "Spec Level" | "Personalization";

function toAduCategory(optionGroup: ReviewConfigurationStep_OptionGroupFragment): AduCategory {
  const { isElevation, isExteriorScheme, isInteriorScheme, isSpecLevel } = optionGroup.group.group.type;
  if (isElevation) return "Elevation";
  if (isExteriorScheme) return "Exterior Scheme";
  if (isInteriorScheme) return "Interior Scheme";
  if (isSpecLevel) return "Spec Level";
  return "Personalization";
}

/**
 * I.e. "5 Beds, 4.5 Baths & 4,250 Sq Ft". Half baths count as .5, and the decimal is dropped when it is a whole number.
 * Exported for testing.
 * */
export function toProgramDataLine(programData: ReviewConfigurationStep_ProgramDataFragment | null | undefined): string {
  const { bedrooms, fullBaths, halfBaths, sellableSqft } = programData ?? {};
  const bedCount = bedrooms ?? 0;
  const bathCount = (fullBaths ?? 0) + (halfBaths ?? 0) / 2;
  const baths = Number.isInteger(bathCount) ? bathCount : bathCount.toFixed(1);
  const sqft = (sellableSqft ?? 0).toLocaleString();
  return `${bedCount} ${pluralize(bedCount, "Bed")}, ${baths} ${pluralize(bathCount, "Bath")} & ${sqft} Sq Ft`;
}
